import {
  DashboardLayout,
  WebDashboardContentSpecBlocksInner,
  BlockRow,
  LayoutColumn,
} from "@coeff/agent-types";
import {
  CollisionDetection,
  DndContext,
  DragEndEvent,
  DragMoveEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { AddBlockButton, AddBlockType } from "./AddBlockButton";
import { AIImageBlock } from "./AIImageBlock";
import { AIInsightsBlock } from "./AIInsightsBlock";
import { BlockSkeleton } from "./BlockSkeleton";
import { ChartBlock } from "./ChartBlock";
import { getChartRebuildSignature } from "./chartUserOverrides";
import { CustomHtmlBlock } from "./CustomHtmlBlock";
import { DashboardWindow } from "./dashboardHelpers";
import { DataTableBlock } from "./DataTableBlock";
import { TableContentHeightReport } from "./dataTableSizing";
import { DraggableBlock, InsertPosition } from "./DraggableBlock";
import {
  addBlockAboveInColumn,
  addBlockBelowInColumn,
  addBlockToColumnStack,
  computeLayoutFromBlocks,
  enforceSectionHeaderIsolation,
  getOrderedBlockIds,
  getRowFlexWidths,
  insertBlockAsNewRow,
  MAX_COLUMNS_PER_ROW,
  mergeLayoutWithBlocks,
  MIN_ROW_HEIGHT,
  ROW_HEIGHT_SECTION_HEADER,
  moveBlockInLayout,
  removeBlockFromLayout,
  snapFlexWidthsTo12ColumnGrid,
  snapLayoutFlexWidths,
  updateRowFlexWidths,
  computeEffectiveRowHeight,
  getDefaultRowHeight,
  getRowBlockTypes,
  isMetricOnlyRow,
} from "./layoutHelpers";
import { MetricCardBlock } from "./MetricCardBlock";
import { SectionHeaderBlock } from "./SectionHeaderBlock";
import { TextBlock } from "./TextBlock";
import { isDarkTheme, Theme, useTheme } from "./themes";
import { ErrorStateDetails } from "./useBlockRender";
import { SlicerState } from "./useDashboardRuntime";
import { useIsMobile } from "./useIsMobile";

/**
 * Gap between blocks on mobile (<640px), per the Figma: 12px between rows, between the two KPI
 * cards that pair up, and between blocks stacked in one column. The desktop row gap stays 24px.
 *
 * A plain number (not a Tailwind class) because DraggableRow applies its gap as an inline style —
 * the column-width formula needs the value arithmetically, and inline styles beat `max-sm:` classes.
 */
const MOBILE_BLOCK_GAP = 12;
const DESKTOP_ROW_GAP = 24;

type Block = WebDashboardContentSpecBlocksInner;

const TODO_RENDER_FUNCTION_JS = "// TODO";

const hasRenderableRenderFunction = (renderFunctionJs?: string): boolean => {
  if (typeof renderFunctionJs !== "string") {
    return false;
  }
  const trimmedRenderFunctionJs = renderFunctionJs.trim();
  return trimmedRenderFunctionJs !== "" && trimmedRenderFunctionJs !== TODO_RENDER_FUNCTION_JS;
};

/**
 * Custom collision detection that finds the droppable closest to the pointer/cursor position.
 * Unlike closestCenter which measures from the center of the dragged item,
 * this measures directly from where the cursor is.
 *
 * @param scrollDelta - Optional scroll delta to adjust pointer coordinates for scroll during drag.
 *                      This compensates for stale droppable rects when scrolling happens.
 */
const createClosestToPointer =
  (scrollDelta?: { x: number; y: number }): CollisionDetection =>
  ({ droppableRects, pointerCoordinates }) => {
    if (!pointerCoordinates) {
      return [];
    }

    // Adjust pointer coordinates by scroll delta to compensate for stale droppable rects
    const pointerX = pointerCoordinates.x + (scrollDelta?.x ?? 0);
    const pointerY = pointerCoordinates.y + (scrollDelta?.y ?? 0);

    let closestId: string | null = null;
    let closestDistance = Infinity;

    for (const [id, rect] of droppableRects) {
      // Calculate the closest point on the rect to the pointer
      const closestX = Math.max(rect.left, Math.min(pointerX, rect.right));
      const closestY = Math.max(rect.top, Math.min(pointerY, rect.bottom));

      // Calculate distance from pointer to closest point on rect
      const dx = pointerX - closestX;
      const dy = pointerY - closestY;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestId = id as string;
      }
    }

    if (closestId === null) {
      return [];
    }

    return [
      {
        id: closestId,
        data: { droppableContainer: droppableRects.get(closestId) },
      },
    ];
  };

interface Props {
  blocks: Block[];
  slicerState: SlicerState;
  targetWindow: DashboardWindow;
  idSuffix: string;
  initialLayout?: DashboardLayout;
  onLayoutChange: (layout: DashboardLayout) => void;
  onBlockDelete: (blockId: string, newLayout: DashboardLayout) => void;
  /**
   * Optional for public dashboards where Explain is disabled. The optional
   * `errorMessage` is supplied by the block error fallback "Learn more" button
   * so the chat agent can be primed with the console error; the context-menu
   * "Explain" entry calls this with only the blockId. `errorStateDetails`
   * carries the BE-attributed structured attribution (e.g. implicated source
   * tabs) for the failure (empty/absent for context-menu and runtime-error
   * flows).
   */
  onExplainBlockInChat?: (
    blockId: string,
    errorMessage?: string,
    errorStateDetails?: ErrorStateDetails | null
  ) => void;
  /**
   * Open the chat sidebar and explain the block (instant if a stored explanation
   * exists, otherwise the agent generates it). Optional for public dashboards
   * where Explain is disabled.
   */
  onExplainInChat?: (blockId: string) => void;
  onEditBlock?: (blockId: string, updates: import("./BlockEditPopover").BlockEditUpdates) => void;
  /** Cache-only write for the popover's controls; saved by onCommitBlockEdits. */
  onApplyBlockEditLocally?: (
    blockId: string,
    updates: import("./BlockEditPopover").BlockEditUpdates
  ) => void;
  /** Ends a block's popover session: one save for everything applied locally. */
  onCommitBlockEdits?: (blockId: string) => void;
  onAddBlock?: (
    blockType: AddBlockType,
    insertAtRowIndex: number,
    prompt?: string,
    adjacentContext?: { block: Block; placement: "after" | "below" }
  ) => void;
  canEdit: boolean;
  /**
   * True while an AI edit run is in flight. Resize handles are hidden for the duration:
   * the agent's save would race the resize (ENG-7141 measured ~330 resize reverts/30d
   * before the OCC merge landed), and even with the merge a same-layout collision would
   * bounce the user's resize with a conflict toast mid-gesture. Drag/delete stay enabled —
   * they're rarer during runs and the server-side merge protects them.
   */
  isAiEditing?: boolean;
  maxRows?: number;
  // When true, indicates that ai_image blocks are currently being regenerated in the
  // background and should show an intermediate "regenerating" state instead of the
  // existing image.
  areAiImagesStale?: boolean;
  /** Block IDs that should show the glowing rim effect and/or image regenerating state */
  refreshingBlockIds?: Set<string>;
  onRendered?: (blockId: string) => void;
  /** When false, show skeleton placeholders instead of real block content */
  isRuntimeInitialized?: boolean;
  /** Called when a block's "Fix this" button is clicked after a render failure */
  onFixBlock?: (blockId: string, errorMessage: string) => void;
  /**
   * Measured natural content height per data table block. Table-only rows
   * auto-collapse to min(stored height, content) — see computeEffectiveRowHeight.
   */
  tableContentHeights?: Record<string, TableContentHeightReport>;
}

interface BlockRendererProps {
  block: Block;
  slicerState: SlicerState;
  targetWindow: DashboardWindow;
  idSuffix: string;
  areAiImagesStale?: boolean;
  isBlockRefreshing?: boolean;
  onRendered?: (blockId: string) => void;
  isRuntimeInitialized?: boolean;
  onFixBlock?: (blockId: string, errorMessage: string) => void;
  /** Wired to the block error fallback "Learn more" button */
  onExplainBlockInChat?: (
    blockId: string,
    errorMessage?: string,
    errorStateDetails?: ErrorStateDetails | null
  ) => void;
}

const BlockRenderer: React.FC<BlockRendererProps> = ({
  block,
  slicerState,
  targetWindow,
  idSuffix,
  areAiImagesStale,
  isBlockRefreshing,
  onRendered,
  isRuntimeInitialized = true,
  onFixBlock,
  onExplainBlockInChat,
}) => {
  const [isRendered, setIsRendered] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [isRerendering, setIsRerendering] = useState(false);

  const handleRenderStart = useCallback(() => {
    setIsRerendering(true);
  }, []);

  const handleRendered = useCallback(
    (blockId: string) => {
      setIsFadingOut(true);
      setTimeout(() => {
        setIsRendered(true);
        setIsFadingOut(false);
      }, 250);
      onRendered?.(blockId);
    },
    [onRendered]
  );

  const handleRenderComplete = useCallback(() => {
    setIsRerendering(false);
  }, []);

  // Only metric_card, chart, data_table run SQL queries and need skeleton + runtime gate.
  // section_header, text_block, ai_image, ai_insights are static and render immediately.
  const isDataBlock = !["section_header", "text_block", "ai_image", "ai_insights"].includes(
    block.type
  );
  const canRenderDataBlock =
    !isDataBlock ||
    (isRuntimeInitialized && hasRenderableRenderFunction((block as any).render_function_js));

  useEffect(() => {
    if (isDataBlock && !canRenderDataBlock) {
      setIsRendered(false);
      setIsFadingOut(false);
      setIsRerendering(false);
    }
  }, [isDataBlock, canRenderDataBlock]);

  // Data blocks wait for runtime init (so slicerState is ready) AND for their render
  // function to fire onRendered. Non-data blocks render immediately — no SQL, no wait.
  const showSkeleton = isDataBlock && (!canRenderDataBlock || !isRendered);

  const title = "title" in block ? block.title : "";
  const label = "label" in block ? block.label : "";

  const shouldGlow = isBlockRefreshing && block.type !== "ai_image";
  const isImageRegenerating =
    block.type === "ai_image" && Boolean(areAiImagesStale || isBlockRefreshing);

  const blockContent = (() => {
    switch (block.type) {
      case "metric_card":
        return (
          <div className={(shouldGlow ? "block-refreshing-wrapper " : "") + "h-full"}>
            <MetricCardBlock
              blockId={block.block_id}
              label={block.label}
              icon={block.icon}
              iconColor={block.icon_color}
              showIcon={block.show_icon}
              showComparison={block.show_comparison}
              renderFunctionJs={block.render_function_js}
              slicerState={slicerState}
              targetWindow={targetWindow}
              idSuffix={idSuffix}
              blockType={block.type}
              errorState={block.error_state}
              errorStateDetails={block.error_state_details as ErrorStateDetails | null | undefined}
              onRendered={handleRendered}
              onRenderStart={handleRenderStart}
              onRenderComplete={handleRenderComplete}
              onFixBlock={onFixBlock}
              onExplainBlockInChat={onExplainBlockInChat}
            />
          </div>
        );
      case "chart":
        return (
          <div className={(shouldGlow ? "block-refreshing-wrapper " : "") + "h-full"}>
            <ChartBlock
              blockId={block.block_id}
              title={block.title}
              description={block.description}
              chartOverridesSignature={getChartRebuildSignature(block.chart_overrides)}
              renderFunctionJs={block.render_function_js}
              slicerState={slicerState}
              targetWindow={targetWindow}
              idSuffix={idSuffix}
              blockType={block.type}
              errorState={block.error_state}
              errorStateDetails={block.error_state_details as ErrorStateDetails | null | undefined}
              onRendered={handleRendered}
              onRenderStart={handleRenderStart}
              onRenderComplete={handleRenderComplete}
              onFixBlock={onFixBlock}
              onExplainBlockInChat={onExplainBlockInChat}
            />
          </div>
        );
      case "data_table":
        return (
          <div className={(shouldGlow ? "block-refreshing-wrapper " : "") + "h-full"}>
            <DataTableBlock
              blockId={block.block_id}
              title={block.title}
              description={block.description}
              renderFunctionJs={block.render_function_js}
              slicerState={slicerState}
              targetWindow={targetWindow}
              idSuffix={idSuffix}
              blockType={block.type}
              errorState={block.error_state}
              errorStateDetails={block.error_state_details as ErrorStateDetails | null | undefined}
              onRendered={handleRendered}
              onRenderStart={handleRenderStart}
              onRenderComplete={handleRenderComplete}
              onFixBlock={onFixBlock}
              onExplainBlockInChat={onExplainBlockInChat}
            />
          </div>
        );
      case "custom_html":
        return (
          <div className={(shouldGlow ? "block-refreshing-wrapper " : "") + "h-full"}>
            <CustomHtmlBlock
              blockId={block.block_id}
              title={block.title}
              renderFunctionJs={block.render_function_js ?? ""}
              slicerState={slicerState}
              targetWindow={targetWindow}
              idSuffix={idSuffix}
              blockType={block.type}
              errorState={block.error_state}
              errorStateDetails={block.error_state_details as ErrorStateDetails | null | undefined}
              onRendered={handleRendered}
              onRenderStart={handleRenderStart}
              onRenderComplete={handleRenderComplete}
              onFixBlock={onFixBlock}
              onExplainBlockInChat={onExplainBlockInChat}
            />
          </div>
        );
      case "ai_insights":
        return (
          <div className={(shouldGlow ? "block-refreshing-wrapper " : "") + "h-full"}>
            <AIInsightsBlock
              blockId={block.block_id}
              title={block.title}
              gradientFrom={block.gradient_from}
              gradientTo={block.gradient_to}
              gradientDirection={block.gradient_direction}
              insights={block.insights}
              targetWindow={targetWindow}
              idSuffix={idSuffix}
            />
          </div>
        );
      case "ai_image":
        return (
          <AIImageBlock
            blockId={block.block_id}
            imageUrl={block.image_url ?? ""}
            aiPrompt={block.ai_prompt}
            idSuffix={idSuffix}
            isRegenerating={isImageRegenerating}
          />
        );
      case "section_header":
        return (
          <div className="h-full">
            <SectionHeaderBlock title={block.title} description={block.description} />
          </div>
        );
      case "text_block":
        return (
          <div className="h-full">
            <TextBlock markdown={block.markdown} />
          </div>
        );
      default:
        return null;
    }
  })();

  if (!isDataBlock) {
    return <>{blockContent}</>;
  }

  // blockContent is only mounted once isRuntimeInitialized is true (so render functions
  // don't fire before slicerState is ready). Once mounted, it stays in the same tree
  // position so React never unmounts/remounts it during the skeleton → real transition.
  // The skeleton overlays on top and fades out; the block fades in simultaneously.
  // Metric cards stay in flow (not `absolute inset-0`) so a long label or status adds to the
  // block's height and grows its metric-only row — see isMetricOnlyRow. The skeleton below
  // still overlays absolutely, so the crossfade is unchanged.
  const contentPositionClassName =
    block.type === "metric_card" ? "relative h-full" : "absolute inset-0";
  return (
    <div className="relative h-full">
      <div
        className={`${contentPositionClassName} transition-opacity duration-200 ${
          isRerendering
            ? "opacity-0 pointer-events-none"
            : isFadingOut
            ? "block-fade-in"
            : isRendered
            ? ""
            : "opacity-0 pointer-events-none"
        }`}
      >
        {canRenderDataBlock && blockContent}
      </div>
      {(showSkeleton || isRerendering) && (
        <div
          className={`absolute inset-0 ${isFadingOut && !isRerendering ? "skeleton-fade-out" : ""}`}
          style={{ zIndex: 1 }}
        >
          <BlockSkeleton
            blockId={block.block_id}
            blockType={block.type}
            chartType={"chart_type" in block ? (block as any).chart_type : undefined}
            title={title!}
            label={label}
          />
        </div>
      )}
    </div>
  );
};

const getResizeHandleBgClassName = (theme: Theme) => {
  if (isDarkTheme(theme.themeId)) {
    return "bg-white/30";
  }
  return "bg-black/30";
};

interface RowResizeHandleProps {
  id: string;
  isDropActive: boolean;
  canResize: boolean;
  rowIndex: number;
  onResizeStart: (rowIndex: number, startY: number) => void;
  onAddBlock?: (
    blockType: AddBlockType,
    insertAtRowIndex: number,
    prompt?: string,
    adjacentContext?: { block: Block; placement: "after" | "below" }
  ) => void;
  /** Block in the row above this handle, used for "below block X" context */
  aboveBlock?: Block;
  targetWindow?: DashboardWindow;
}

const RowResizeHandle: React.FC<RowResizeHandleProps> = ({
  id,
  isDropActive,
  canResize: canResizeProp,
  rowIndex,
  onResizeStart,
  onAddBlock,
  aboveBlock,
  targetWindow,
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id,
    disabled: !isDropActive,
  });

  const { theme } = useTheme();
  const isMobile = useIsMobile(targetWindow);
  // Row resizing and the add-block button are desktop-only. Both live behind `canResize` and are
  // revealed by `opacity-0 group-hover:opacity-100`, which on a touch device never fires — leaving
  // an invisible-but-hit-testable resize strip (plus an explicit invisible hit area below) and an
  // invisible `pointer-events-auto` add button. Folding `!isMobile` into `canResize` here rather
  // than at the call sites means every downstream use — including `handleMouseDown` — is covered,
  // and the component falls through to its plain view-mode spacer with no layout change.
  const canResize = canResizeProp && !isMobile;
  const [isAddBlockOpen, setIsAddBlockOpen] = useState(false);

  const resizeHandleBgClassName = getResizeHandleBgClassName(theme);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!isDropActive && canResize && rowIndex > 0) {
        e.preventDefault();
        onResizeStart(rowIndex - 1, e.clientY);
      }
    },
    [isDropActive, canResize, rowIndex, onResizeStart]
  );

  const adjacentCtx = aboveBlock ? { block: aboveBlock, placement: "below" as const } : undefined;

  const addButton =
    onAddBlock && targetWindow && !isDropActive ? (
      <AddBlockButton
        onAddBlock={type => onAddBlock(type, rowIndex, undefined, adjacentCtx)}
        onAddBlockWithPrompt={(type, prompt) => onAddBlock(type, rowIndex, prompt, adjacentCtx)}
        onOpenStateChange={setIsAddBlockOpen}
        targetWindow={targetWindow}
        direction="horizontal"
      />
    ) : null;

  // When dragging, show drop zone style
  if (isDropActive) {
    return (
      <div
        ref={setNodeRef}
        className={`
          h-1 border-1 border-transparent
          ${isOver ? "bg-blue-500 border-blue-400" : "bg-transparent"}
        `}
      />
    );
  }

  // In edit mode, when not dragging and there's a row above, show resize handle with add button
  if (canResize && rowIndex > 0) {
    return (
      <div
        ref={setNodeRef}
        // max-sm:hidden on every RowResizeHandle branch, so none of them can contribute layout at
        // mobile widths — see the view-mode spacer below for why it must be `display: none` rather
        // than a zeroed height. Belt-and-braces here: `canResize` already folds in `!isMobile`, so
        // this branch is unreachable below 640px today. Kept uniform deliberately, so that changing
        // the `canResize` guard cannot silently reintroduce a gap-eating spacer.
        className="group relative h-3 -my-2 max-sm:hidden flex items-center justify-center cursor-ns-resize"
        data-testid="row-resize-handle"
        onMouseDown={handleMouseDown}
      >
        {/* Invisible hit area */}
        <div className="absolute inset-x-0 -inset-y-1" />

        <div
          className={`absolute inset-y-0 left-0 right-0 flex items-center transition-opacity duration-150 ${
            isAddBlockOpen ? "opacity-100" : "opacity-0 group-hover:opacity-100"
          }`}
        >
          <div className={`flex-grow h-0.5 rounded-full ${resizeHandleBgClassName}`} />
          {addButton && (
            <div
              className="shrink-0 mx-1 pointer-events-auto z-20"
              onMouseDown={e => e.stopPropagation()}
            >
              {addButton}
            </div>
          )}
          <div className={`flex-grow h-0.5 rounded-full ${resizeHandleBgClassName}`} />
        </div>
      </div>
    );
  }

  // In edit mode, first row (rowIndex=0) — show add button with flanking lines
  if (canResize && rowIndex === 0 && addButton) {
    return (
      <div
        ref={setNodeRef}
        className="group relative h-3 -my-1 max-sm:hidden flex items-center justify-center"
      >
        <div
          className={`absolute inset-y-0 left-0 right-0 flex items-center transition-opacity duration-150 ${
            isAddBlockOpen ? "opacity-100" : "opacity-0 group-hover:opacity-100"
          }`}
        >
          <div className={`flex-grow h-0.5 rounded-full ${resizeHandleBgClassName}`} />
          <div className="shrink-0 mx-1 z-20">{addButton}</div>
          <div className={`flex-grow h-0.5 rounded-full ${resizeHandleBgClassName}`} />
        </div>
      </div>
    );
  }

  // In view mode, rows after the first use same negative margin trick as edit mode
  // to maintain consistent visual spacing. Edit mode uses h-3 -my-2 for the resize handle.
  //
  // max-sm:hidden — it has to be `display: none`, not a zeroed height. This spacer is a flex item
  // between two rows in the row stack, so it takes a gap on BOTH sides: at `h-3 -my-2` (10.5px
  // tall, -7px margins in the 14px-root iframe) a 12px stack gap renders as 20.5px, and merely
  // zeroing height/margins still leaves 12 + 0 + 12 = 24px. Removing it from the flex flow leaves
  // one 12px gap between consecutive rows, which is the spacing the mobile Figma asks for.
  if (rowIndex > 0) {
    return <div ref={setNodeRef} className="h-3 -my-2 max-sm:hidden" />;
  }

  // First drop zone (before first row) - just a minimal spacer. Also hidden on mobile: as a flex
  // item it would otherwise add a leading 12px gap above the first row.
  return <div ref={setNodeRef} className="h-0 max-sm:hidden" />;
};

const MIN_BLOCK_WIDTH_RATIO = 0.15; // Minimum width as ratio of row (15%)

interface BlockResizeHandleProps {
  rowIndex: number;
  columnIndex: number; // Handle is AFTER this column
  gapSize: number; // CSS gap size in pixels
  onResizeStart: (rowIndex: number, columnIndex: number, startX: number) => void;
  onAddBlock?: (
    blockType: AddBlockType,
    insertAtRowIndex: number,
    prompt?: string,
    adjacentContext?: { block: Block; placement: "after" | "below" }
  ) => void;
  /** Block to the left of this handle, used for "after block X" context */
  leftBlock?: Block;
  targetWindow?: DashboardWindow;
}

/**
 * Horizontal resize handle that overlays on top of CSS gap between columns.
 * Uses negative margin to position itself over the gap without affecting layout.
 * Also renders a centered "+" add button on hover.
 */
const BlockResizeHandle: React.FC<BlockResizeHandleProps> = ({
  rowIndex,
  columnIndex,
  gapSize,
  onResizeStart,
  onAddBlock,
  leftBlock,
  targetWindow,
}) => {
  const { theme } = useTheme();
  const [isAddBlockOpen, setIsAddBlockOpen] = useState(false);

  const resizeHandleBgClassName = getResizeHandleBgClassName(theme);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onResizeStart(rowIndex, columnIndex, e.clientX);
    },
    [rowIndex, columnIndex, onResizeStart]
  );

  return (
    // `max-sm:hidden` lives here (on the flex child) rather than on a wrapper so the negative
    // marginLeft still cancels the row's flex `gap` around this handle — otherwise a wrapper
    // becomes the flex child, the margin is stranded inside, and every multi-column row overflows
    // by one gap per handle.
    <div
      className="relative shrink-0 max-sm:hidden"
      style={{ width: 0, marginLeft: `-${gapSize}px`, marginRight: 0 }}
    >
      <div
        className={`absolute inset-y-0 flex items-center justify-center group ${
          isAddBlockOpen ? "z-50" : "z-10"
        }`}
        style={{ left: 0, width: `${gapSize}px` }}
        data-testid="block-resize-handle"
        onMouseDown={handleMouseDown}
      >
        <div
          className={`absolute inset-0 flex flex-col items-center transition-opacity duration-150 ${
            isAddBlockOpen ? "opacity-100" : "opacity-0 group-hover:opacity-100"
          }`}
        >
          <div className={`flex-grow w-0.5 rounded-full ${resizeHandleBgClassName}`} />
          {onAddBlock && targetWindow && (
            <div
              className="shrink-0 my-1 pointer-events-auto z-20"
              onMouseDown={e => e.stopPropagation()}
            >
              <AddBlockButton
                onAddBlock={type => {
                  const ctx = leftBlock
                    ? { block: leftBlock, placement: "after" as const }
                    : undefined;
                  onAddBlock(type, rowIndex, undefined, ctx);
                }}
                onAddBlockWithPrompt={(type, prompt) => {
                  const ctx = leftBlock
                    ? { block: leftBlock, placement: "after" as const }
                    : undefined;
                  onAddBlock(type, rowIndex, prompt, ctx);
                }}
                onOpenStateChange={setIsAddBlockOpen}
                targetWindow={targetWindow}
                direction="vertical"
              />
            </div>
          )}
          <div className={`flex-grow w-0.5 rounded-full ${resizeHandleBgClassName}`} />
        </div>
      </div>
    </div>
  );
};

/**
 * Info about the current drop target and direction
 */
interface DropInfo {
  overId: string;
  direction: InsertPosition;
}

/**
 * Renders a vertical stack of blocks within a column.
 */
interface ColumnStackProps {
  column: LayoutColumn;
  columnIndex: number;
  rowIndex: number;
  blockMap: Map<string, Block>;
  slicerState: SlicerState;
  targetWindow: DashboardWindow;
  idSuffix: string;
  activeId: string | null;
  canEdit: boolean;
  onDelete: (blockId: string) => void;
  /**
   * Optional for public dashboards where Explain is disabled. Opens the chat
   * sidebar and explains the block (instant if a stored explanation exists).
   */
  onExplainInChat?: (blockId: string) => void;
  /**
   * Block-error-fallback variant of the Explain callback. Forwarded to
   * BlockRenderer so MetricCard/Chart/DataTable blocks can wire the
   * "Learn more" button on render failure.
   */
  onExplainBlockInChat?: (
    blockId: string,
    errorMessage?: string,
    errorStateDetails?: ErrorStateDetails | null
  ) => void;
  onEditBlock?: (blockId: string, updates: import("./BlockEditPopover").BlockEditUpdates) => void;
  /** Cache-only write for the popover's controls; saved by onCommitBlockEdits. */
  onApplyBlockEditLocally?: (
    blockId: string,
    updates: import("./BlockEditPopover").BlockEditUpdates
  ) => void;
  /** Ends a block's popover session: one save for everything applied locally. */
  onCommitBlockEdits?: (blockId: string) => void;
  isHorizontalResizing?: boolean;
  isDragging?: boolean;
  dropInfo?: DropInfo | null;
  areAiImagesStale?: boolean;
  refreshingBlockIds?: Set<string>;
  onRendered?: (blockId: string) => void;
  isRuntimeInitialized?: boolean;
  onFixBlock?: (blockId: string, errorMessage: string) => void;
  /** The row is metric-only and sizes to its content (see isMetricOnlyRow). */
  sizeToContent?: boolean;
}

const ColumnStack: React.FC<ColumnStackProps> = ({
  column,
  columnIndex,
  rowIndex,
  blockMap,
  slicerState,
  targetWindow,
  idSuffix,
  activeId,
  canEdit,
  onDelete,
  onExplainInChat,
  onExplainBlockInChat,
  onEditBlock,
  onApplyBlockEditLocally,
  onCommitBlockEdits,
  isHorizontalResizing,
  isDragging,
  dropInfo,
  areAiImagesStale,
  refreshingBlockIds,
  onRendered,
  isRuntimeInitialized,
  onFixBlock,
  sizeToContent,
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `column-${column.id}`,
    data: {
      type: "column",
      columnId: column.id,
      rowIndex,
      columnIndex,
    },
    disabled: !isDragging || !canEdit,
  });

  // Calculate the active index within this column
  const activeIndex = activeId !== null ? column.blockIds.indexOf(activeId) : -1;

  // Equal height for each block in the stack
  const blockCount = column.blockIds.length;
  const gapSize = 8; // gap-2 = 8px
  const totalGapHeight = (blockCount - 1) * gapSize;

  return (
    <div
      ref={setNodeRef}
      // Mobile: release the fixed h-full (which relied on the row's fixed pixel height) so a
      // stacked column can grow to fit its children, each sized via its own mobile min-height
      // below. A content-sized row keeps h-full: its column is stretched to the wrapped line's
      // height, so the cards paired on that line still match.
      className={`flex flex-col gap-2 max-sm:gap-[12px] h-full ${
        sizeToContent ? "" : "max-sm:!h-auto"
      } transition-all duration-200 ${
        isOver && isDragging ? "ring-2 ring-blue-400 ring-inset rounded-lg bg-blue-50/50" : ""
      }`}
      data-column-id={column.id}
    >
      {column.blockIds.map((blockId, blockIndex) => {
        const block = blockMap.get(blockId);
        if (!block) {
          return null;
        }

        // Get drop direction for this specific block
        const blockDropDirection =
          dropInfo && dropInfo.overId === blockId ? dropInfo.direction : null;

        // On mobile, blocks stacked in one column stop equally dividing a fixed-height parent
        // (flex: 1 1 0) and instead get their own fixed, type-appropriate height (NOT just a
        // min-height floor) so the column can grow with an auto height. This has to be a firm
        // `height`, not `min-height`: block internals (e.g. ChartBlock's chart-container/canvas,
        // which use height:100%/flex-1 chains down to `themed-dashboard-card`'s h-full) need a
        // definite ancestor height to resolve percentage heights against — a min-height floor on
        // an auto-basis flex item does not count as "definite" for that purpose, so charts were
        // rendering into a 0-height canvas until this was a real `height`.
        //
        // Metric cards in a metric-only row are the exception: they have no percentage-height
        // internals, so a min-height floor lets a long label or status grow the card instead of
        // clipping it, and `grow` fills the column when the card paired with it on the same
        // wrapped line is taller, so the two match.
        const mobileBlockHeight = getDefaultRowHeight([block.type]);
        const mobileBlockSizeClassName = sizeToContent
          ? `max-sm:!grow max-sm:!h-auto max-sm:!min-h-[${mobileBlockHeight}px]`
          : `max-sm:!grow-0 max-sm:!h-[${mobileBlockHeight}px]`;

        return (
          <DraggableBlock
            key={blockId}
            id={blockId}
            index={blockIndex}
            activeIndex={activeIndex}
            canEdit={canEdit}
            onDelete={onDelete}
            onExplainInChat={onExplainInChat}
            onEditBlock={onEditBlock}
            onApplyBlockEditLocally={onApplyBlockEditLocally}
            onCommitBlockEdits={onCommitBlockEdits}
            dropDirection={blockDropDirection}
            targetWindow={targetWindow}
            block={block}
            className={`max-sm:!shrink-0 max-sm:!basis-auto ${mobileBlockSizeClassName}`}
            style={{
              flex: `1 1 0`,
              // In a content-sized row, `auto` makes each card's content its floor, so the column
              // (and with it the row) grows to fit; `1 1 0` still splits any extra height evenly.
              minHeight: sizeToContent ? "auto" : 0,
              // Disable transitions during horizontal resize
              transition: isHorizontalResizing ? "none" : undefined,
            }}
          >
            <BlockRenderer
              block={block}
              slicerState={slicerState}
              targetWindow={targetWindow}
              idSuffix={idSuffix}
              areAiImagesStale={areAiImagesStale}
              isBlockRefreshing={refreshingBlockIds?.has(block.block_id)}
              onRendered={onRendered}
              isRuntimeInitialized={isRuntimeInitialized}
              onFixBlock={onFixBlock}
              onExplainBlockInChat={onExplainBlockInChat}
            />
          </DraggableBlock>
        );
      })}
    </div>
  );
};

interface DraggableRowProps {
  row: BlockRow;
  rowIndex: number;
  blockMap: Map<string, Block>;
  slicerState: SlicerState;
  targetWindow: DashboardWindow;
  idSuffix: string;
  activeId: string | null;
  canEdit: boolean;
  onDelete: (blockId: string) => void;
  /**
   * Optional for public dashboards where Explain is disabled. The optional
   * `errorMessage` arg is supplied by the block error fallback "Learn more"
   * button so the chat agent can be primed with the console error; the
   * context-menu "Explain" entry calls this with only the blockId.
   * `errorStateDetails` carries the BE-attributed structured attribution (e.g.
   * implicated source tabs) for the failure so the chat handler can include
   * them in the backend message.
   */
  onExplainBlockInChat?: (
    blockId: string,
    errorMessage?: string,
    errorStateDetails?: ErrorStateDetails | null
  ) => void;
  /** Open chat and explain the block (see ColumnStackProps). */
  onExplainInChat?: (blockId: string) => void;
  onEditBlock?: (blockId: string, updates: import("./BlockEditPopover").BlockEditUpdates) => void;
  /** Cache-only write for the popover's controls; saved by onCommitBlockEdits. */
  onApplyBlockEditLocally?: (
    blockId: string,
    updates: import("./BlockEditPopover").BlockEditUpdates
  ) => void;
  /** Ends a block's popover session: one save for everything applied locally. */
  onCommitBlockEdits?: (blockId: string) => void;
  onAddBlock?: (
    blockType: AddBlockType,
    insertAtRowIndex: number,
    prompt?: string,
    adjacentContext?: { block: Block; placement: "after" | "below" }
  ) => void;
  /** See DraggableBlocksSection Props — hides resize handles while an AI edit runs. */
  isAiEditing?: boolean;
  isVerticalResizing?: boolean;
  isHorizontalResizing?: boolean;
  onHorizontalResizeStart?: (rowIndex: number, columnIndex: number, startX: number) => void;
  dropInfo?: DropInfo | null;
  areAiImagesStale?: boolean;
  refreshingBlockIds?: Set<string>;
  onRendered?: (blockId: string) => void;
  isRuntimeInitialized?: boolean;
  onFixBlock?: (blockId: string, errorMessage: string) => void;
  /** See DraggableBlocksSection Props — drives table-only row auto-collapse. */
  tableContentHeights?: Record<string, TableContentHeightReport>;
}

const DraggableRow: React.FC<DraggableRowProps> = ({
  row,
  rowIndex,
  blockMap,
  slicerState,
  targetWindow,
  idSuffix,
  activeId,
  canEdit,
  onDelete,
  onExplainBlockInChat,
  onExplainInChat,
  onEditBlock,
  onApplyBlockEditLocally,
  onCommitBlockEdits,
  onAddBlock,
  isAiEditing,
  isVerticalResizing,
  isHorizontalResizing,
  onHorizontalResizeStart,
  dropInfo,
  areAiImagesStale,
  refreshingBlockIds,
  onRendered,
  isRuntimeInitialized,
  onFixBlock,
  tableContentHeights,
}) => {
  // Get flex widths (either from layout or derived from grid_cols)
  const flexWidths = useMemo(() => getRowFlexWidths(row, blockMap), [row, blockMap]);

  // Determine if this row can have horizontal resize handles
  // (need at least 2 columns to resize between them)
  const canHorizontalResize = canEdit && !isAiEditing && row.columns.length > 1;

  const isDragging = activeId !== null;

  const isSectionHeaderRow = useMemo(() => {
    const blockIds = row.columns.flatMap(col => col.blockIds);
    return blockIds.length > 0 && blockIds.every(id => blockMap.get(id)?.type === "section_header");
  }, [row, blockMap]);

  const defaultRowHeight = useMemo(
    () => getDefaultRowHeight(getRowBlockTypes(row, blockMap)),
    [row, blockMap]
  );

  // Table-only rows auto-collapse to their measured content (stored height stays
  // the cap). Suspended while the user is drag-resizing this row so the drag
  // tracks the pointer instead of fighting the collapse.
  const effectiveRowHeight = useMemo(
    () =>
      isVerticalResizing
        ? row.height ?? defaultRowHeight
        : computeEffectiveRowHeight(row, blockMap, tableContentHeights, defaultRowHeight),
    [row, blockMap, tableContentHeights, defaultRowHeight, isVerticalResizing]
  );

  const sizeToContent = useMemo(() => isMetricOnlyRow(row, blockMap), [row, blockMap]);

  // Metric-only rows treat the stored height as a minimum, so the tallest card's content sets
  // the row height and every card stretches to match it.
  const rowStyle: React.CSSProperties = {
    ...(sizeToContent
      ? { height: "auto", minHeight: Math.max(effectiveRowHeight, MIN_ROW_HEIGHT) }
      : {
          height: effectiveRowHeight,
          minHeight: isSectionHeaderRow ? ROW_HEIGHT_SECTION_HEADER : MIN_ROW_HEIGHT,
        }),
    // Disable transitions during resize for smoother dragging
    transition: isVerticalResizing || isHorizontalResizing ? "none" : undefined,
  };

  // Use CSS gap for consistent spacing. When in edit mode, we overlay resize handles
  // on top of the gaps for drag-to-resize functionality.
  // This is the gap BOTH between columns and, once the row wraps on mobile, between wrapped lines.
  const isMobile = useIsMobile(targetWindow);
  const gapSize = isMobile ? MOBILE_BLOCK_GAP : DESKTOP_ROW_GAP;

  return (
    <div
      // Mobile (<640px): rows wrap instead of proportionally compressing every column, and the
      // fixed pixel height (tuned for the desktop side-by-side layout) is released so the row can
      // grow to fit stacked/wrapped children. Each column below carries its own mobile min-height.
      className="flex flex-nowrap items-stretch max-sm:flex-wrap max-sm:!h-auto max-sm:!min-h-0"
      data-row-id={row.rowId}
      style={{ ...rowStyle, gap: `${gapSize}px` }}
    >
      {row.columns.map((column, columnIndex) => {
        const columnSpan = flexWidths[columnIndex] ?? 4;

        // Calculate width using 12-column grid formula that accounts for gaps.
        // This ensures items align across rows regardless of how many items per row.
        // Formula: width = N * (100% + gap) / 12 - gap
        // Where N is the number of columns the item spans.
        const columnWidth = `calc(${columnSpan} * (100% + ${gapSize}px) / 12 - ${gapSize}px)`;

        // Mobile layout rule: a column made up entirely of metric_card blocks gets 2-per-row
        // (50% width, minus half the gap); everything else — charts, tables, text, insights,
        // section headers, and any mixed/stacked column — gets the full row width (1-per-row).
        // With `flex-wrap` on the row, this alone produces the "KPIs pair up, everything else
        // stacks" mobile layout without touching the persisted `layout`/`flexWidths` state.
        const columnBlockTypes = column.blockIds
          .map(id => blockMap.get(id)?.type)
          .filter((t): t is Block["type"] => t != null);
        const isMobileKpiColumn =
          columnBlockTypes.length > 0 && columnBlockTypes.every(t => t === "metric_card");
        // basis is 50% minus HALF the row gap: two cards share one gap between them.
        const mobileColumnClassName = isMobileKpiColumn
          ? "max-sm:!grow-0 max-sm:!shrink-0 max-sm:!basis-[calc(50%-6px)] max-sm:!h-auto"
          : "max-sm:!grow-0 max-sm:!shrink-0 max-sm:!basis-full max-sm:!h-auto";

        return (
          <React.Fragment key={column.id}>
            <div
              className={mobileColumnClassName}
              style={{
                flex: `0 0 ${columnWidth}`,
                minWidth: 0,
                // An auto-height row can't resolve 100%; leaving it unset lets the column stretch.
                height: sizeToContent ? undefined : "100%",
                transition: isHorizontalResizing ? "none" : undefined,
              }}
            >
              <ColumnStack
                column={column}
                columnIndex={columnIndex}
                rowIndex={rowIndex}
                blockMap={blockMap}
                slicerState={slicerState}
                targetWindow={targetWindow}
                idSuffix={idSuffix}
                activeId={activeId}
                canEdit={canEdit}
                onDelete={onDelete}
                onExplainInChat={onExplainInChat}
                onExplainBlockInChat={onExplainBlockInChat}
                onEditBlock={onEditBlock}
                onApplyBlockEditLocally={onApplyBlockEditLocally}
                onCommitBlockEdits={onCommitBlockEdits}
                isHorizontalResizing={isHorizontalResizing}
                isDragging={isDragging}
                dropInfo={dropInfo}
                areAiImagesStale={areAiImagesStale}
                refreshingBlockIds={refreshingBlockIds}
                onRendered={onRendered}
                isRuntimeInitialized={isRuntimeInitialized}
                onFixBlock={onFixBlock}
                sizeToContent={sizeToContent}
              />
            </div>
            {canHorizontalResize &&
              columnIndex < row.columns.length - 1 &&
              onHorizontalResizeStart && (
                // Handle hides itself on mobile via `max-sm:hidden` on its own root (see below) —
                // no wrapper, so its negative margin keeps cancelling the row's flex gap on desktop.
                // Resize handles assume a single-line row of side-by-side columns; once columns wrap
                // on mobile their absolute-in-gap positioning no longer lines up, and drag-to-resize
                // editing isn't part of the mobile experience for this pass.
                <BlockResizeHandle
                  rowIndex={rowIndex}
                  columnIndex={columnIndex}
                  gapSize={gapSize}
                  onResizeStart={onHorizontalResizeStart}
                  onAddBlock={!isDragging ? onAddBlock : undefined}
                  leftBlock={blockMap.get(column.blockIds[0])}
                  targetWindow={targetWindow}
                />
              )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

interface VerticalResizeState {
  rowIndex: number;
  startY: number;
  /** The row's RENDERED height at mousedown — the drag continues from what the
   * user sees (an auto-collapsed table row renders below its stored height). */
  startHeight: number;
  /** The row's stored height at mousedown (possibly unset), restored when the
   * pointer never moves so a stray click on the handle can't silently lower
   * the stored cap to the collapsed height. */
  originalStoredHeight: number | null | undefined;
}

interface HorizontalResizeState {
  rowIndex: number;
  columnIndex: number; // The divider is AFTER this column
  startX: number;
  startFlexWidths: number[]; // Snapshot of flexWidths at drag start
  rowWidth: number; // Row width in pixels at drag start
}

export const DraggableBlocksSection: React.FC<Props> = ({
  blocks,
  slicerState,
  targetWindow,
  idSuffix,
  initialLayout,
  onLayoutChange,
  onBlockDelete,
  onExplainBlockInChat,
  onExplainInChat,
  onEditBlock,
  onApplyBlockEditLocally,
  onCommitBlockEdits,
  onAddBlock,
  canEdit,
  isAiEditing,
  maxRows,
  areAiImagesStale,
  refreshingBlockIds,
  onRendered,
  isRuntimeInitialized,
  onFixBlock,
  tableContentHeights,
}) => {
  const canResize = canEdit && !isAiEditing;

  const [layout, setLayout] = useState<DashboardLayout>(() => {
    // If initialLayout is provided, merge it with current blocks to handle
    // blocks that may have been added or removed since the layout was saved
    if (initialLayout && initialLayout.rows.length > 0) {
      return mergeLayoutWithBlocks(initialLayout, blocks);
    }
    // Otherwise, compute layout from blocks
    return computeLayoutFromBlocks(blocks);
  });

  const [activeId, setActiveId] = useState<string | null>(null);
  const [dropInfo, setDropInfo] = useState<DropInfo | null>(null);

  // Vertical resize state
  const [verticalResizeState, setVerticalResizeState] = useState<VerticalResizeState | null>(null);
  // Horizontal resize state
  const [horizontalResizeState, setHorizontalResizeState] = useState<HorizontalResizeState | null>(
    null
  );
  const rowRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const layoutRef = useRef(layout);
  layoutRef.current = layout;

  // Scroll tracking during drag - compensates for stale droppable rects when scrolling
  const scrollStartRef = useRef({ x: 0, y: 0 });
  const [scrollDelta, setScrollDelta] = useState({ x: 0, y: 0 });

  // Track actual pointer position in viewport coordinates via pointermove events.
  const pointerPositionRef = useRef<{ x: number; y: number } | null>(null);
  const cursorYRef = useRef<number | null>(null);

  // Track the current dnd-kit "over" block ID so the pointermove handler can
  // continuously recalculate the drop zone at full framerate.
  const overIdRef = useRef<string | null>(null);
  const lastDropInfoRef = useRef<{ overId: string; direction: InsertPosition } | null>(null);

  const blockMap = useMemo(() => {
    const map = new Map<string, Block>();
    blocks.forEach(block => {
      map.set(block.block_id, block);
    });
    return map;
  }, [blocks]);

  // Stringify initialLayout for dependency comparison to detect deep changes
  // (React does reference equality for objects, which can miss content changes during undo)
  const initialLayoutKey = JSON.stringify(initialLayout);

  // Recompute layout when blocks change (e.g., new blocks added)
  // Also snap to 12-column grid to fix any misaligned row layouts in the DB that don't sum to 12.
  useEffect(() => {
    let newLayout: DashboardLayout;
    let hasNewBlocks = false;
    // If initialLayout is provided, merge it with current blocks
    if (initialLayout && initialLayout.rows.length > 0) {
      const layoutBlockIds = new Set(getOrderedBlockIds(initialLayout));
      hasNewBlocks = blocks.some(b => !layoutBlockIds.has(b.block_id));
      newLayout = enforceSectionHeaderIsolation(
        mergeLayoutWithBlocks(initialLayout, blocks),
        blockMap
      );
    } else {
      newLayout = computeLayoutFromBlocks(blocks);
    }
    // Snap to 12-column grid to ensure proper alignment
    const snappedLayout = snapLayoutFlexWidths(newLayout, blockMap);
    setLayout(snappedLayout);
    // Persist the corrected layout when new blocks were placed by the merge,
    // so the backend reflects the actual positions rather than a stale layout.
    if (hasNewBlocks) {
      onLayoutChange?.(snappedLayout);
    }
  }, [blocks, initialLayoutKey, blockMap]);

  // Handle vertical resize start
  const handleResizeStart = useCallback(
    (rowIndex: number, startY: number) => {
      const row = layout.rows[rowIndex];

      if (!row) {
        return;
      }

      // Seed from the RENDERED height (DOM first): an auto-collapsed table row
      // displays below its stored height, and the drag must continue from what
      // the user sees — seeding from row.height would jump the row up to the
      // stored cap on mousedown.
      const rowEl = rowRefs.current.get(rowIndex);
      const currentHeight =
        rowEl?.offsetHeight ?? row.height ?? getDefaultRowHeight(getRowBlockTypes(row, blockMap));

      // Pin the row's live height to that same rendered value for the duration
      // of the drag. DraggableRow suspends auto-collapse while resizing and
      // shows row.height — without this pin it would show the stored cap and
      // the row would snap before the first mousemove.
      setLayout(prevLayout => ({
        ...prevLayout,
        rows: prevLayout.rows.map((r, index) =>
          index === rowIndex ? { ...r, height: currentHeight } : r
        ),
      }));

      setVerticalResizeState({
        rowIndex,
        startY,
        startHeight: currentHeight,
        originalStoredHeight: row.height,
      });
    },
    [layout.rows, blockMap]
  );

  // Handle horizontal resize start
  const handleHorizontalResizeStart = useCallback(
    (rowIndex: number, columnIndex: number, startX: number) => {
      const row = layout.rows[rowIndex];

      if (!row || row.columns.length < 2) {
        return;
      }

      // Get row width from DOM
      const rowEl = rowRefs.current.get(rowIndex);
      const rowWidth = rowEl?.offsetWidth ?? 800;

      // Get current flex widths (either from layout or derived from grid_cols)
      const currentFlexWidths = getRowFlexWidths(row, blockMap);

      setHorizontalResizeState({
        rowIndex,
        columnIndex,
        startX,
        startFlexWidths: currentFlexWidths,
        rowWidth,
      });
    },
    [layout.rows, blockMap]
  );

  // Important: Use targetWindow.document since content is in an iframe
  // Vertical resize mouse events
  useEffect(() => {
    if (!verticalResizeState) {
      return;
    }

    const iframeDoc = targetWindow.document;

    // Set on the first real drag movement. A mousedown that never moves must
    // not persist anything: resize start pins row.height to the RENDERED
    // (possibly auto-collapsed) height, and saving that on a stray click would
    // silently lower the user's stored cap to the collapsed height.
    let pointerMoved = false;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaY = e.clientY - verticalResizeState.startY;
      if (deltaY !== 0) {
        pointerMoved = true;
      }
      const resizingRow = layout.rows[verticalResizeState.rowIndex];
      const resizingBlockIds = resizingRow?.columns.flatMap(col => col.blockIds) ?? [];
      const isResizingSectionHeader =
        resizingBlockIds.length > 0 &&
        resizingBlockIds.every(id => blockMap.get(id)?.type === "section_header");
      const minH = isResizingSectionHeader ? ROW_HEIGHT_SECTION_HEADER : MIN_ROW_HEIGHT;
      const newHeight = Math.max(minH, verticalResizeState.startHeight + deltaY);

      setLayout(prevLayout => ({
        ...prevLayout,
        rows: prevLayout.rows.map((row, index) =>
          index === verticalResizeState.rowIndex ? { ...row, height: newHeight } : row
        ),
      }));
    };

    const handleMouseUp = () => {
      if (!pointerMoved) {
        // No drag happened — undo the resize-start pin so the row's stored
        // height (or lack of one) is exactly as before, and skip the save.
        setLayout(prevLayout => ({
          ...prevLayout,
          rows: prevLayout.rows.map((row, index) =>
            index === verticalResizeState.rowIndex
              ? { ...row, height: verticalResizeState.originalStoredHeight }
              : row
          ),
        }));
        setVerticalResizeState(null);
        return;
      }
      // Notify parent of layout change using ref to get current value
      onLayoutChange?.(layoutRef.current);
      setVerticalResizeState(null);
    };

    iframeDoc.addEventListener("mousemove", handleMouseMove);
    iframeDoc.addEventListener("mouseup", handleMouseUp);

    // Add cursor style to body during resize
    iframeDoc.body.style.cursor = "ns-resize";
    iframeDoc.body.style.userSelect = "none";

    return () => {
      iframeDoc.removeEventListener("mousemove", handleMouseMove);
      iframeDoc.removeEventListener("mouseup", handleMouseUp);
      iframeDoc.body.style.cursor = "";
      iframeDoc.body.style.userSelect = "";
    };
  }, [verticalResizeState, onLayoutChange, targetWindow]);

  // Horizontal resize mouse events
  useEffect(() => {
    if (!horizontalResizeState) {
      return;
    }

    const iframeDoc = targetWindow.document;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - horizontalResizeState.startX;
      const { rowIndex, columnIndex, startFlexWidths, rowWidth } = horizontalResizeState;

      // Calculate the change as a ratio of total flex width
      const totalFlexWidth = startFlexWidths.reduce((sum, w) => sum + w, 0);
      const deltaRatio = deltaX / rowWidth;
      const deltaFlexWidth = deltaRatio * totalFlexWidth;

      // Adjust the two adjacent columns
      const leftWidth = startFlexWidths[columnIndex];
      const rightWidth = startFlexWidths[columnIndex + 1];

      // Calculate new widths
      let newLeftWidth = leftWidth + deltaFlexWidth;
      let newRightWidth = rightWidth - deltaFlexWidth;

      // Enforce minimum widths (as ratio of total)
      const minFlexWidth = MIN_BLOCK_WIDTH_RATIO * totalFlexWidth;
      if (newLeftWidth < minFlexWidth) {
        newLeftWidth = minFlexWidth;
        newRightWidth = leftWidth + rightWidth - minFlexWidth;
      } else if (newRightWidth < minFlexWidth) {
        newRightWidth = minFlexWidth;
        newLeftWidth = leftWidth + rightWidth - minFlexWidth;
      }

      // Create new flex widths array
      const newFlexWidths = [...startFlexWidths];
      newFlexWidths[columnIndex] = newLeftWidth;
      newFlexWidths[columnIndex + 1] = newRightWidth;

      setLayout(prevLayout => updateRowFlexWidths(prevLayout, rowIndex, newFlexWidths));
    };

    const handleMouseUp = () => {
      // Snap to 12-column grid before notifying parent
      const { rowIndex } = horizontalResizeState;
      const currentRow = layoutRef.current.rows[rowIndex];
      if (currentRow) {
        const currentFlexWidths = currentRow.flexWidths ?? [];
        const snappedFlexWidths = snapFlexWidthsTo12ColumnGrid(currentFlexWidths);
        const snappedLayout = updateRowFlexWidths(layoutRef.current, rowIndex, snappedFlexWidths);
        setLayout(snappedLayout);
        onLayoutChange?.(snappedLayout);
      } else {
        onLayoutChange?.(layoutRef.current);
      }
      setHorizontalResizeState(null);
    };

    iframeDoc.addEventListener("mousemove", handleMouseMove);
    iframeDoc.addEventListener("mouseup", handleMouseUp);

    // Add cursor style to body during resize
    iframeDoc.body.style.cursor = "ew-resize";
    iframeDoc.body.style.userSelect = "none";

    return () => {
      iframeDoc.removeEventListener("mousemove", handleMouseMove);
      iframeDoc.removeEventListener("mouseup", handleMouseUp);
      iframeDoc.body.style.cursor = "";
      iframeDoc.body.style.userSelect = "";
    };
  }, [horizontalResizeState, onLayoutChange, targetWindow]);

  // Track scroll during drag for accurate collision detection
  useEffect(() => {
    if (!activeId) {
      return;
    }

    const iframeDoc = targetWindow.document;
    const scrollElement = iframeDoc.documentElement;

    const handleScroll = () => {
      const newScrollDelta = {
        x: scrollElement.scrollLeft - scrollStartRef.current.x,
        y: scrollElement.scrollTop - scrollStartRef.current.y,
      };
      setScrollDelta(newScrollDelta);
    };

    // Listen on both the document and window to catch all scroll events
    iframeDoc.addEventListener("scroll", handleScroll, { passive: true });
    targetWindow.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      iframeDoc.removeEventListener("scroll", handleScroll);
      targetWindow.removeEventListener("scroll", handleScroll);
    };
  }, [activeId, targetWindow]);

  // Continuously recalculate the drop zone at pointermove frequency.
  // This avoids lag during fast drags since dnd-kit's onDragMove is throttled.
  const computeDropZone = useCallback(
    (pointer: { x: number; y: number }) => {
      const overId = overIdRef.current;
      if (!overId) {
        if (lastDropInfoRef.current !== null) {
          lastDropInfoRef.current = null;
          setDropInfo(null);
        }
        return;
      }

      const el = targetWindow.document.querySelector(`[data-block-id="${overId}"]`);
      if (!el) {
        if (lastDropInfoRef.current !== null) {
          lastDropInfoRef.current = null;
          setDropInfo(null);
        }
        return;
      }

      const rect = el.getBoundingClientRect();
      const rows = layoutRef.current.rows;
      const currentActiveId = activeId;

      let allowVerticalStacking = true;
      let allowHorizontalNewColumn = true;
      for (const row of rows) {
        if (row.columns.length === 1 && row.columns[0].blockIds.includes(overId)) {
          allowVerticalStacking = false;
          break;
        }
        for (const column of row.columns) {
          if (column.blockIds.includes(overId)) {
            const activeInRow = currentActiveId
              ? row.columns.some(col => col.blockIds.includes(currentActiveId))
              : false;
            if (row.columns.length >= MAX_COLUMNS_PER_ROW && !activeInRow) {
              allowHorizontalNewColumn = false;
            }
            break;
          }
        }
      }

      const relativeX = pointer.x - rect.left;
      const relativeY = pointer.y - rect.top;
      const blockWidth = rect.width;
      const blockHeight = rect.height;

      const leftThreshold = blockWidth * 0.35;
      const rightThreshold = blockWidth * 0.65;
      const topThreshold = blockHeight * 0.3;
      const bottomThreshold = blockHeight * 0.7;

      let direction: InsertPosition;

      if (
        allowVerticalStacking &&
        relativeY < topThreshold &&
        relativeX > leftThreshold &&
        relativeX < rightThreshold
      ) {
        direction = InsertPosition.Above;
      } else if (
        allowVerticalStacking &&
        relativeY > bottomThreshold &&
        relativeX > leftThreshold &&
        relativeX < rightThreshold
      ) {
        direction = InsertPosition.Below;
      } else if (allowHorizontalNewColumn) {
        direction = relativeX < blockWidth / 2 ? InsertPosition.Before : InsertPosition.After;
      } else {
        if (lastDropInfoRef.current !== null) {
          lastDropInfoRef.current = null;
          setDropInfo(null);
        }
        return;
      }

      const prev = lastDropInfoRef.current;
      if (!prev || prev.overId !== overId || prev.direction !== direction) {
        lastDropInfoRef.current = { overId, direction };
        setDropInfo({ overId, direction });
      }
    },
    [activeId, targetWindow]
  );

  // Track real pointer position during drag and recalculate drop zone continuously
  useEffect(() => {
    if (!activeId) {
      pointerPositionRef.current = null;
      cursorYRef.current = null;
      overIdRef.current = null;
      return;
    }

    const handlePointerMove = (e: PointerEvent) => {
      const pos = { x: e.clientX, y: e.clientY };
      pointerPositionRef.current = pos;
      cursorYRef.current = e.clientY;
      computeDropZone(pos);
    };

    targetWindow.addEventListener("pointermove", handlePointerMove);
    return () => targetWindow.removeEventListener("pointermove", handlePointerMove);
  }, [activeId, targetWindow, computeDropZone]);

  // Custom auto-scroll during drag (dnd-kit's built-in auto-scroll doesn't work in iframe/portal context)
  useEffect(() => {
    if (!activeId) {
      return;
    }

    const scrollElement = targetWindow.document.documentElement;
    const threshold = 100;
    const maxSpeed = 15;

    let animationFrameId: number;

    const autoScroll = () => {
      const cursorY = cursorYRef.current;
      if (cursorY !== null) {
        const viewportHeight = targetWindow.innerHeight;
        const maxScrollTop = scrollElement.scrollHeight - scrollElement.clientHeight;

        if (cursorY < threshold && scrollElement.scrollTop > 0) {
          const ratio = 1 - cursorY / threshold;
          const speed = Math.ceil(maxSpeed * ratio);
          scrollElement.scrollTop = Math.max(0, scrollElement.scrollTop - speed);
        } else if (cursorY > viewportHeight - threshold && scrollElement.scrollTop < maxScrollTop) {
          const ratio = 1 - (viewportHeight - cursorY) / threshold;
          const speed = Math.ceil(maxSpeed * ratio);
          scrollElement.scrollTop = Math.min(maxScrollTop, scrollElement.scrollTop + speed);
        }
      }

      animationFrameId = requestAnimationFrame(autoScroll);
    };

    animationFrameId = requestAnimationFrame(autoScroll);

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [activeId, targetWindow]);

  // Store row ref callback
  const setRowRef = useCallback((index: number, el: HTMLDivElement | null) => {
    if (el) {
      rowRefs.current.set(index, el);
    } else {
      rowRefs.current.delete(index);
    }
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      // Capture initial scroll position for scroll-aware collision detection
      const scrollElement = targetWindow.document.documentElement;
      scrollStartRef.current = {
        x: scrollElement.scrollLeft,
        y: scrollElement.scrollTop,
      };
      setScrollDelta({ x: 0, y: 0 });

      setActiveId(event.active.id as string);
    },
    [targetWindow]
  );

  // Update the tracked "over" block whenever dnd-kit reports a new one.
  // The actual drop-zone calculation runs continuously in the pointermove handler.
  const handleDragMove = useCallback(
    (event: DragMoveEvent) => {
      const { over } = event;

      if (!over || over.id === event.active.id) {
        overIdRef.current = null;
        lastDropInfoRef.current = null;
        setDropInfo(null);
        return;
      }

      const overId = over.id as string;

      if (overId.startsWith("dropzone-") || overId.startsWith("column-")) {
        overIdRef.current = null;
        lastDropInfoRef.current = null;
        setDropInfo(null);
        return;
      }

      overIdRef.current = overId;

      // Also immediately recalculate with current pointer to keep in sync
      const pointer = pointerPositionRef.current;
      if (pointer) {
        computeDropZone(pointer);
      }
    },
    [computeDropZone]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      // Capture dropInfo before clearing it
      const currentDropInfo = dropInfo;

      setActiveId(null);
      setDropInfo(null);
      setScrollDelta({ x: 0, y: 0 });
      overIdRef.current = null;
      lastDropInfoRef.current = null;
      cursorYRef.current = null;

      if (!over || active.id === over.id) {
        return;
      }

      const activeBlockId = active.id as string;
      const overId = over.id as string;

      let newLayout: DashboardLayout;

      // Check if dropping on a row drop zone (new row creation)
      if (overId.startsWith("dropzone-")) {
        const rowIndex = parseInt(overId.replace("dropzone-", ""), 10);
        newLayout = insertBlockAsNewRow(layout, activeBlockId, rowIndex);
      }
      // Check if dropping on a column (for vertical stacking)
      else if (overId.startsWith("column-")) {
        const columnId = overId.replace("column-", "");
        newLayout = addBlockToColumnStack(layout, activeBlockId, columnId, "bottom");
      }
      // Check if dropping above a block (vertical stacking in same column)
      else if (currentDropInfo?.direction === InsertPosition.Above) {
        newLayout = addBlockAboveInColumn(layout, activeBlockId, overId);
      }
      // Check if dropping below a block (vertical stacking in same column)
      else if (currentDropInfo?.direction === InsertPosition.Below) {
        newLayout = addBlockBelowInColumn(layout, activeBlockId, overId);
      }
      // Horizontal move (before/after another block)
      else {
        // Check if over is a row or a block
        const isOverRow = overId.startsWith("row-");
        const overRowId = isOverRow ? overId : null;
        const overBlockIdForMove = isOverRow ? null : overId;

        // Determine if we should insert before the target column
        const insertBefore = currentDropInfo?.direction === InsertPosition.Before;

        newLayout = moveBlockInLayout(
          layout,
          activeBlockId,
          overBlockIdForMove,
          overRowId,
          blockMap,
          undefined, // overColumnId
          insertBefore
        );
      }

      // Ensure section headers remain in their own full-width row
      const isolatedLayout = enforceSectionHeaderIsolation(newLayout, blockMap);

      // Snap all rows to 12-column grid to ensure proper alignment
      // This handles cases where columns are added/removed during drag
      const snappedLayout = snapLayoutFlexWidths(isolatedLayout, blockMap);

      setLayout(snappedLayout);
      onLayoutChange?.(snappedLayout);
    },
    [layout, onLayoutChange, blockMap, dropInfo]
  );

  const handleDelete = useCallback(
    (blockId: string) => {
      const newLayout = removeBlockFromLayout(layout, blockId);

      setLayout(newLayout);

      // Pass both blockId and newLayout so parent can handle as single operation
      onBlockDelete?.(blockId, newLayout);
    },
    [layout, onBlockDelete]
  );

  const activeBlock = activeId ? blockMap.get(activeId) : null;

  // Get all block IDs for the single top-level SortableContext
  const allBlockIds = useMemo(
    () => layout.rows.flatMap(row => row.columns.flatMap(col => col.blockIds)),
    [layout.rows]
  );

  // Create scroll-aware collision detection that adjusts for scroll during drag
  const collisionDetection = useMemo(() => createClosestToPointer(scrollDelta), [scrollDelta]);

  if (blocks.length === 0) {
    if (canEdit && onAddBlock) {
      return (
        <div className="flex flex-col items-center justify-center py-24 gap-5 text-center">
          <div className="w-16 h-16 rounded-2xl bg-main-foreground/5 flex items-center justify-center">
            <span className="material-icons !text-3xl text-main-muted-foreground">
              dashboard_customize
            </span>
          </div>
          <div>
            <p className="text-base font-semibold text-main-foreground mb-1">No blocks yet</p>
            <p className="text-sm text-main-muted-foreground">
              Add your first block to start building your dashboard.
            </p>
          </div>
          <AddBlockButton
            onAddBlock={type => onAddBlock(type, 0)}
            onAddBlockWithPrompt={(type, prompt) => onAddBlock(type, 0, prompt)}
            targetWindow={targetWindow}
            direction="horizontal"
          />
        </div>
      );
    }
    return null;
  }

  const isDragging = activeId !== null;

  const isVerticalResizing = verticalResizeState !== null;
  const isHorizontalResizing = horizontalResizeState !== null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
      autoScroll={{
        // Enable aggressive auto-scrolling for vertical movement
        threshold: {
          x: 0.1,
          y: 0.15, // Start scrolling when within 15% of viewport edge
        },
        acceleration: 20, // Scroll faster as cursor gets closer to edge
      }}
    >
      <SortableContext items={allBlockIds} strategy={rectSortingStrategy}>
        {/* max-sm:mb-[8px]: #mainContent has no bottom padding or border, so this trailing margin
            collapses through it and is what actually sets the gap below the last block at the
            bottom of the dashboard frame. At mb-6 (21px in the 14px-root iframe) it dominated the
            frame's 8px mobile inset. */}
        <div className="flex flex-col gap-3 max-sm:gap-[12px] mb-6 max-sm:mb-[8px]">
          <RowResizeHandle
            id="dropzone-0"
            isDropActive={isDragging && !!canEdit}
            canResize={canResize}
            rowIndex={0}
            onResizeStart={handleResizeStart}
            onAddBlock={canEdit && !isDragging ? onAddBlock : undefined}
            targetWindow={targetWindow}
          />

          {(maxRows ? layout.rows.slice(0, maxRows) : layout.rows).map((row, index) => {
            const firstBlockId = row.columns[0]?.blockIds[0];
            const rowAboveBlock = firstBlockId ? blockMap.get(firstBlockId) : undefined;

            return (
              <React.Fragment key={row.rowId}>
                <div ref={el => setRowRef(index, el)}>
                  <DraggableRow
                    row={row}
                    rowIndex={index}
                    blockMap={blockMap}
                    slicerState={slicerState}
                    targetWindow={targetWindow}
                    idSuffix={idSuffix}
                    activeId={activeId}
                    canEdit={canEdit}
                    onDelete={handleDelete}
                    onExplainBlockInChat={onExplainBlockInChat}
                    onExplainInChat={onExplainInChat}
                    onEditBlock={onEditBlock}
                    onApplyBlockEditLocally={onApplyBlockEditLocally}
                    onCommitBlockEdits={onCommitBlockEdits}
                    onAddBlock={onAddBlock}
                    isAiEditing={isAiEditing}
                    isVerticalResizing={
                      isVerticalResizing && verticalResizeState?.rowIndex === index
                    }
                    isHorizontalResizing={
                      isHorizontalResizing && horizontalResizeState?.rowIndex === index
                    }
                    onHorizontalResizeStart={handleHorizontalResizeStart}
                    dropInfo={dropInfo}
                    areAiImagesStale={areAiImagesStale}
                    refreshingBlockIds={refreshingBlockIds}
                    onRendered={onRendered}
                    isRuntimeInitialized={isRuntimeInitialized}
                    onFixBlock={onFixBlock}
                    tableContentHeights={tableContentHeights}
                  />
                </div>
                <RowResizeHandle
                  id={`dropzone-${index + 1}`}
                  isDropActive={isDragging && !!canEdit}
                  canResize={canResize}
                  rowIndex={index + 1}
                  onResizeStart={handleResizeStart}
                  onAddBlock={canEdit && !isDragging ? onAddBlock : undefined}
                  aboveBlock={rowAboveBlock}
                  targetWindow={targetWindow}
                />
              </React.Fragment>
            );
          })}
        </div>
      </SortableContext>

      <DragOverlay>
        {activeBlock ? (
          <div className="opacity-80 shadow-2xl">
            <BlockRenderer
              block={activeBlock}
              slicerState={slicerState}
              targetWindow={targetWindow}
              idSuffix={idSuffix}
              areAiImagesStale={areAiImagesStale}
              isBlockRefreshing={refreshingBlockIds?.has(activeBlock.block_id)}
            />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};
